import { Col, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import moment from "moment";
import { useGetQuotationFormConfig } from "api/queries/quotation-form/useGetQuotationFormConfig";
import { ShipmentTypes } from "constants/";
import { IQuotationData } from "pages/provider/QuotationDetail/QuotationType";
import MultiStopScheduleSummary from "./MultiStopScheduleSummary";

const DATE_FORMAT = "YYYY-MM-DD, HH:mm";

interface ScheduleSumaryProps {
  quoteRequestDetails: IQuotationData;
}

export default function ScheduleSumary({ quoteRequestDetails }: ScheduleSumaryProps) {
  const { watch, getValues } = useFormContext();
  const id = useParams().id?.toUpperCase();
  const { data: quotationConfigData } = useGetQuotationFormConfig(id!, {
    enabled: !!id,
    refetchOnMount: false,
  });
  const selectedFreightModeData = quotationConfigData?.freight_modes?.find(
    (item: any) => item.value === getValues("service.freight_mode"),
  );

  const schedule = watch("schedule") ?? {};

  const isAirFreight =
    ["air_charter", "obc", "nfo", "regular_air"].includes(watch("service.freight_mode")) ||
    selectedFreightModeData?.type === "air";
  const isOceanFreight =
    ["ocean_freight"].includes(watch("service.freight_mode")) || selectedFreightModeData?.type === "ocean";

  const isMultiStop = quoteRequestDetails?.organization?.shipment_type === ShipmentTypes.MULTISTOP;
  const stops = quoteRequestDetails?.schedule?.stops || [];

  const flights = schedule.airline ?? [];

  const pickupDateTime = `${schedule.pickup_date} ${schedule.pickup_time}`;
  const deliveryDateTime = `${schedule.delivery_date} ${schedule.delivery_time}`;
  const confirmationDateTime = `${schedule.rfq_deadline_date} ${schedule.rfq_deadline_time}`;

  const multiStopInstructions = quoteRequestDetails?.schedule?.multistop_instructions;

  return (
    <>
      <div className="mb-4 font-weight-bold">
        <FormattedMessage id="general.schedule" defaultMessage="Schedule" />
      </div>

      <div className="font-b3 space-y-2">
        {multiStopInstructions && (
          <Row className="font-b3 mb-xl">
            <Col className="text-secondary">
              <FormattedMessage
                id="new-request.schedule.multistop.multistopInstructions"
                defaultMessage="Multi-stop instructions"
              />
            </Col>
            <Col>{multiStopInstructions}</Col>
          </Row>
        )}

        {isMultiStop && stops.length > 0 ? (
          <MultiStopScheduleSummary
            stops={stops}
            schedule={schedule}
            isOceanFreight={isOceanFreight}
            isAirFreight={isAirFreight}
          />
        ) : (
          <>
            <Row>
              <Col className="text-secondary">
                <FormattedMessage id="quote.provider.latestPickupTime" defaultMessage="Latest pickup time" />
              </Col>
              <Col>{moment(pickupDateTime, "YYYY-MM-DD HH:mm:ss").format(DATE_FORMAT)}</Col>
            </Row>

            <Row>
              <Col className="text-secondary">
                <FormattedMessage id="quote.provider.estimatedDeliveryTime" defaultMessage="Estimated delivery time" />
              </Col>
              <Col>{moment(deliveryDateTime, "YYYY-MM-DD HH:mm:ss").format(DATE_FORMAT)}</Col>
            </Row>

            <Row>
              <Col className="text-secondary">
                <FormattedMessage id="quote.provider.quotationValidUntil" defaultMessage="Quotation is valid until" />
              </Col>
              <Col>{moment(confirmationDateTime, "YYYY-MM-DD HH:mm:ss").format(DATE_FORMAT)}</Col>
            </Row>

            <hr className="my-4" />

            {isOceanFreight && (
              <>
                <Row>
                  <Col className="text-secondary">
                    <FormattedMessage id="quote.provider.vesselNumber" defaultMessage="Vessel number / IMO" />
                  </Col>
                  <Col>{schedule.vessel_number}</Col>
                </Row>
                <Row>
                  <Col className="text-secondary">
                    <FormattedMessage id="quote.provider.vesselDepartureDate" defaultMessage="Vessel departure date" />
                  </Col>
                  <Col>{moment(`${schedule.vessel_departure_date}`, "YYYY-MM-DD").format("YYYY-MM-DD")}</Col>
                </Row>
                <Row>
                  <Col className="text-secondary">
                    <FormattedMessage id="quote.provider.departurePort" defaultMessage="Departure port" />
                  </Col>
                  <Col>{schedule.departure_port_code}</Col>
                </Row>
                <Row>
                  <Col className="text-secondary">
                    <FormattedMessage id="quote.provider.vesselArrivalDate" defaultMessage="Vessel arrival date" />
                  </Col>
                  <Col>{moment(`${schedule.vessel_arrival_date}`, "YYYY-MM-DD").format("YYYY-MM-DD")}</Col>
                </Row>
                <Row>
                  <Col className="text-secondary">
                    <FormattedMessage id="quote.provider.arrivalPort" defaultMessage="Arrival port" />
                  </Col>
                  <Col>{schedule.arrival_port_code}</Col>
                </Row>
              </>
            )}

            {isAirFreight && flights.length > 0 && (
              <>
                <p className="font-b4">
                  <FormattedMessage id="quotation-form.flights" defaultMessage="Flights" />
                </p>
                {flights.map((flight: any, index: number) => (
                  <div key={index} className="mb-3xl">
                    <p className="font-b3">
                      <FormattedMessage id="quotation.form.flight" defaultMessage="Flight" /> {index + 1}
                    </p>
                    <Row>
                      <Col className="text-secondary">
                        <FormattedMessage id="quote.provider.schedule.airline" defaultMessage="Airline" />
                      </Col>
                      <Col>
                        {flight?.airline_code}, {flight?.flight_number}
                      </Col>
                    </Row>
                    <Row>
                      <Col className="text-secondary">
                        <FormattedMessage id="quote.provider.schedule.arrivalDate" defaultMessage="Arrival date" />
                      </Col>
                      <Col>
                        {flight?.arrival_airport} ({flight?.arrival_airport_code})
                      </Col>
                    </Row>
                    <Row>
                      <Col className="text-secondary">
                        <FormattedMessage id="quote.provider.schedule.departureAirport" defaultMessage="Departure airport" />
                      </Col>
                      <Col>
                        {moment(`${flight.arrival_date} ${flight.arrival_time}`, "YYYY-MM-DD HH:mm:ss").format(DATE_FORMAT)}
                      </Col>
                    </Row>
                    <Row>
                      <Col className="text-secondary">
                        <FormattedMessage id="quote.provider.schedule.departureDate" defaultMessage="Departure date" />
                      </Col>
                      <Col>
                        {flight?.departure_airport} ({flight?.departure_airport_code})
                      </Col>
                    </Row>
                    <Row>
                      <Col className="text-secondary">
                        <FormattedMessage id="quote.provider.schedule.departureAirport" defaultMessage="Departure airport" />
                      </Col>
                      <Col>
                        {moment(`${flight.departure_date} ${flight.departure_time}`, "YYYY-MM-DD HH:mm:ss").format(
                          DATE_FORMAT,
                        )}
                      </Col>
                    </Row>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}
